import type { ApolloClient } from '@apollo/client';
import type { PostStatusChangedPayload } from '../../interfaces/websocket.interface';

interface EventBus {
  on(type: string, cb: (data: any) => void): () => void;
  emit(type: string, data: any): void;
}

interface ReportResolvedPayload {
  postId: string;
  authorId: string;
  verdict: 'DISMISS' | 'REWRITE' | 'REMOVE';
  templateKey: string;
  headline: string;
}

// TRIL-82: server emits this from reportPost.mutation.ts after a
// successful reserveAssignment. Mirrors the server-side `data` shape.
interface ReportAssignedPayload {
  reportId: string;
  postId: string;
  topic: string;
  reason: string;
  category: string;
  assignedAt: string;
  assignedModeratorId: string;
}

interface Deps {
  currentUserId: string | null;
  apolloClient: ApolloClient<unknown>;
  showToast: (msg: {
    title: string;
    body: string;
    href?: string;
    variant?: 'default' | 'warning';
  }) => void;
}

export function registerModerationHandlers(
  eventBus: EventBus,
  deps: Deps,
): () => void {
  const offResolved = eventBus.on(
    'report_resolved',
    (payload: ReportResolvedPayload) => {
      if (payload.authorId !== deps.currentUserId) return;

      deps.showToast({
        title:
          payload.verdict === 'REMOVE'
            ? 'Your post was removed'
            : 'Your post was reviewed',
        body: payload.headline,
        href:
          payload.verdict === 'REWRITE'
            ? `/post/${payload.postId}/edit-rewrite`
            : `/post/${payload.postId}`,
        variant: 'warning',
      });

      // Evict the cached Post entity so the refetch below hits the
      // server with the updated moderation state (and so any unrelated
      // consumer of the cached Post sees fresh data).
      deps.apolloClient.cache.evict({
        id: deps.apolloClient.cache.identify({
          __typename: 'Post',
          id: payload.postId,
        }),
      });
      deps.apolloClient.cache.gc();

      // TRIL-203: the cache.evict + gc above is sufficient — any active
      // useQuery(getPostById) watcher refetches reactively, and the next
      // mount of the post detail page hits the server via cache-and-network.
      // The previous TRIL-96 eager network-only refetch existed only to
      // populate the Redux dispatch payload (now removed); deleting the
      // IIFE drops a wasted round-trip with no behavior change.
    },
  );

  // TRIL-82: live moderator-queue refresh. Filter to the assigned
  // moderator's session so other connected moderators don't get a
  // toast meant for someone else. Toast links to the moderation queue
  // and we evict the cached `moderatorQueue` field so the next visit
  // (or an in-flight watchQuery) picks up the new entry.
  const offAssigned = eventBus.on(
    'report_assigned',
    (payload: ReportAssignedPayload) => {
      if (payload.assignedModeratorId !== deps.currentUserId) return;

      deps.showToast({
        title: 'New report assigned',
        body: `${humanizeReason(payload.reason)} — ${payload.topic}`,
        href: '/moderation',
      });

      deps.apolloClient.cache.evict({
        id: 'ROOT_QUERY',
        fieldName: 'moderatorQueue',
      });
      deps.apolloClient.cache.gc();
    },
  );

  // TRIL-83: handler for post_status_changed events.
  // Drives Apollo cache. The ReaderModerationBanner subscribes to this
  // same eventBus event independently to drive its state machine.
  const offStatusChanged = eventBus.on(
    'post_status_changed',
    (payload: PostStatusChangedPayload) => {
      // 1. Apollo cache: patch moderationStatus (and score when present) on the cached Post entity
      const id = deps.apolloClient.cache.identify({
        __typename: 'Post',
        id: payload.postId,
      });
      if (id) {
        deps.apolloClient.cache.modify({
          id,
          fields: {
            moderationStatus: () => payload.status,
            ...(typeof payload.score === 'number'
              ? { score: () => payload.score! }
              : {}),
          },
        });
      }

      // 2. Issue A (non-author path): when republish broadcasts PUBLISHED,
      //    null out Post:{id}.moderation in the Apollo cache so the detail
      //    banner dismisses for readers and moderators who have the post
      //    open. The cache.modify at step 1 already set moderationStatus =
      //    'PUBLISHED'; this also clears the moderation object so cached
      //    banner data is removed.
      if (payload.status === 'PUBLISHED') {
        deps.apolloClient.cache.modify({
          id,
          fields: {
            moderation: () => null,
          },
        });
      }
    },
  );

  return () => {
    offResolved();
    offAssigned();
    offStatusChanged();
  };
}

/**
 * Convert a `ReportReason` enum value (SCREAMING_SNAKE) into a
 * sentence-cased label suitable for a toast body. Mirrors the casing
 * pattern used elsewhere in moderation UI; doesn't import the full
 * enum to keep this file dependency-light.
 */
function humanizeReason(reason: string): string {
  if (!reason) return 'Report';
  return reason
    .split('_')
    .map((part) =>
      part.length === 0
        ? part
        : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
    )
    .join(' ');
}
